import {ApiClient} from '@hconnect/apiclient'
import {useQuery} from '@tanstack/react-query'
import {Moment} from 'moment-timezone'

import {getSchedulePath} from '../api'
import type {Schedule} from '../api'

export type MomentRange = [start: Moment, end: Moment]

export const SCHEDULE_QUERY_KEY = 'schedule'

interface FetchCurrentScheduleParams {
  plantId: string
  range: MomentRange
  signal?: AbortSignal
}

export const getCurrentScheduleQueryKey = (
  plantId: string,
  [startOfPlan, endOfPlan]: MomentRange
) =>
  [
    SCHEDULE_QUERY_KEY,
    {plantCode: plantId, start: startOfPlan.toISOString(), end: endOfPlan.toISOString()}
  ] as const

export const fetchCurrentSchedule = async ({
  plantId,
  range: [startOfPlan, endOfPlan],
  signal
}: FetchCurrentScheduleParams): Promise<Schedule> => {
  const path = getSchedulePath()
  const response = await ApiClient.axiosInstance.get<Schedule>(path, {
    params: {
      plantCode: plantId,
      start: startOfPlan.toISOString(),
      end: endOfPlan.toISOString()
    },
    signal
  })
  return response.data
}

interface UseCurrentScheduleQueryParams {
  plantId: string
  range: MomentRange
  enabled?: boolean
  throwOnError?: boolean
}

export const useCurrentScheduleQuery = <TSelect = Schedule>({
  plantId,
  range,
  enabled = true,
  throwOnError = true,
  select
}: UseCurrentScheduleQueryParams & {
  select?: (data: Schedule) => TSelect
}) => {
  return useQuery<Schedule, unknown, TSelect>({
    queryKey: getCurrentScheduleQueryKey(plantId, range),
    queryFn: ({signal}) => fetchCurrentSchedule({plantId, range, signal}),
    throwOnError,
    enabled,
    // Several components in the planning chart tree (PlanningChartCard, PlanningChartGantt,
    // ScheduleItemsContainer, EditScheduleItemDialog) independently call this hook with the same
    // queryKey, but they mount at slightly different times during a single page load (some are
    // gated behind other pending queries). With the default staleTime of 0, every one of those
    // later mounts is treated as "stale" and triggers its own refetch-on-mount, which flips
    // `isFetching` back to true for a moment right after the initial load and disables schedule
    // item interactions (see ScheduleItemsContainer's `isScheduleItemReadOnly`) even though valid
    // data is already cached and displayed. A short staleTime avoids this redundant refetch while
    // still allowing explicit invalidation (e.g. after submitting/editing the schedule) to force a
    // refetch immediately, since invalidateQueries ignores staleTime.
    staleTime: 30_000,
    ...(enabled ? {} : {gcTime: 0}),
    ...(select ? {select} : {})
  })
}
